import type { ReactNode } from 'react'

import { Message, type TMessageProps } from '@/src/elements/message'
import { FEED_ITEM_TYPE } from '@/src/features/prayer-space/models/prayerSpace.constants'
import { ICON } from '@/src/general/enums/icons'
import { getLocaleDateFormat, getStrictDiffInDays } from '@/src/general/primitives/dates/dayjs'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'
import { useTranslate } from '@/src/lang/useTranslate'

import { COMMUNITY_LIFETIME } from '../../../models/community.constants'
import type { TCommunitySubscriptionCardProps } from './CommunitySubscriptionCard.types'

/**
 * About community dates...
 *
 * - `joinedAt` (= feedItem.pushedAt) is the day the user clicked the button to join the community (to either start it immediately or schedule it for later)
 * - `community.subscription.fromDate` is the up-to-date day the user started the retreat/novena (the day he received the first publication)
 * - `feedItem.subscription.fromDate` is the day the user was supposed to start the retreat/novena when the feed item was created
 * - `community.fromDate` is the date the community has originally started
 */

export const CommunitySubscriptionCardMessage = ({
  communityFeedItem,
  communityFeedItem: {
    community: { lifetime },
  },
}: TCommunitySubscriptionCardProps): ReactNode => {
  const { t } = useTranslate()

  if (communityFeedItem.type === FEED_ITEM_TYPE.COMMUNITY_LEFT)
    return (
      <Message colors="secondary" iconName={ICON.CROSS}>
        {t('community.leave.success', { lifetime })}
      </Message>
    )

  // ...the community has been joined or scheduled/rescheduled

  const joinedCommonProps: TMessageProps = { colors: 'success', iconName: ICON.CHECK_CIRCLE_BROKEN }

  if (lifetime === COMMUNITY_LIFETIME.EVERLASTING)
    // Everlasting community cannot be scheduled or rescheduled => no further complexity
    return <Message {...joinedCommonProps}>{t('community.join.success.message', { lifetime })}</Message>

  // ...the community is a retreat or a novena

  if (!communityFeedItem.subscription)
    // Retreats and novenas feed items should have a subscription
    return <Message {...joinedCommonProps}>{t('community.join.error.no-subscription')}</Message>

  const nbDaysBetweenJoinDayAndStartDay = Math.ceil(
    getStrictDiffInDays(
      new Date(),
      getDateWithoutTimezone(communityFeedItem.subscription.fromDate)?.toDate() || new Date(),
    ),
  )

  // If user began the noveana/retreat on the same day he has joined
  // - either by clicking the "Join today" button
  // - or by joining it on the start day
  if (nbDaysBetweenJoinDayAndStartDay === 0)
    // Display "today" message
    return (
      <Message {...joinedCommonProps}>
        {t('community.join.confirmation.today', {
          lifetime,
          rescheduled: communityFeedItem.type === FEED_ITEM_TYPE.COMMUNITY_RESCHEDULED,
        })}
      </Message>
    )

  // ...the user did not start the novena/retreat on the same day he joined

  // If the subscription will start some day after the user joined (the user scheduled its subscription for later)
  if (nbDaysBetweenJoinDayAndStartDay > 0)
    // Display the "soon" message
    return (
      <Message {...joinedCommonProps}>
        {t('community.join.confirmation.soon', {
          lifetime,
          date: getLocaleDateFormat(getDateWithoutTimezone(communityFeedItem.subscription?.fromDate)),
          nbDays: nbDaysBetweenJoinDayAndStartDay,
          rescheduled: communityFeedItem.type === FEED_ITEM_TYPE.COMMUNITY_RESCHEDULED,
        })}
      </Message>
    )

  // nbDaysBetweenJoinDayAndStartDay < 0: the subscription's start day is in the past.
  // This covers both joining a community already in progress (subscription.fromDate ===
  // community.fromDate) and a scheduled near-future start that has since elapsed
  // (subscription.fromDate !== community.fromDate). Display the "in progress" message in both cases.
  return (
    <Message {...joinedCommonProps}>
      {t('community.join.confirmation.in-progress', {
        lifetime,
        rescheduled: communityFeedItem.type === FEED_ITEM_TYPE.COMMUNITY_RESCHEDULED,
      })}
    </Message>
  )
}
